/**
 * Responsibility: Implement auth store behavior for the ui/stores surface.
 * Scope: Owns ui/stores concerns only; cross-surface orchestration is excluded.
 */
import { 
  getAppImportMetaEnv, 
  supabase,
  checkWhitelist as backendCheckWhitelist,
  type AnyQuestUser 
} from "@anyquest/shared-core"

// ============================================================
// AUTH-STORE.TS — Supabase auth singleton wrapper
// Tracks sign-in status + user + whitelist gate.
// ============================================================

export type AuthStatus = 'loading' | 'signed-in' | 'signed-out' | 'blocked'
let authStatusValue: AuthStatus = 'loading'
let authUserValue: AnyQuestUser | null = null
let authInitialized = false
const subscribers = new Set<() => void>()
const appEnv = getAppImportMetaEnv()
const E2E_MOCK_MODE = appEnv.VITE_E2E_MOCK_MODE === '1'
const SUPABASE_READY =
  !!appEnv.VITE_SUPABASE_URL &&
  !!appEnv.VITE_SUPABASE_ANON_KEY &&
  !String(appEnv.VITE_SUPABASE_ANON_KEY).includes('placeholder') &&
  !String(appEnv.VITE_SUPABASE_ANON_KEY).includes('your-supabase-anon-key')
const AUTH_FALLBACK_MODE = E2E_MOCK_MODE || !SUPABASE_READY
const E2E_MOCK_AUTH_KEY = 'am2_e2e_mock_auth'

let logicCacheRuntimePromise: Promise<typeof import('../../engine/logic-cache')> | null = null
let persistenceServicePromise: Promise<typeof import('../../services/persistence-service')> | null = null

function loadPersistenceService() {
  if (!persistenceServicePromise) persistenceServicePromise = import('../../services/persistence-service')
  return persistenceServicePromise
}

function loadLogicCacheRuntime() {
  if (!logicCacheRuntimePromise) logicCacheRuntimePromise = import('../../engine/logic-cache')
  return logicCacheRuntimePromise
}

function notifySubscribers(): void {
  subscribers.forEach(fn => fn())
}

async function checkWhitelist(uid: string): Promise<boolean> {
  return backendCheckWhitelist(uid)
}

async function initAuth() {
  if (authInitialized) return
  authInitialized = true

  if (AUTH_FALLBACK_MODE) {
    authUserValue = e2eGuestUser()
    authStatusValue = 'signed-in'
    notifySubscribers()
    return
  }
  
  if (E2E_MOCK_MODE) {
    // ... same mock logic ...
    try {
      const mockState = sessionStorage.getItem(E2E_MOCK_AUTH_KEY)
      if (mockState === 'guest') {
        authUserValue = e2eGuestUser()
        authStatusValue = 'signed-in'
      } else if (mockState === 'admin') {
        authUserValue = e2eAdminUser()
        authStatusValue = 'signed-in'
      } else {
        authUserValue = null
        authStatusValue = 'signed-out'
      }
    } catch {
      authStatusValue = 'signed-out'
      authUserValue = null
    }
    notifySubscribers()
    return
  }

  // Supabase Auth Listener
  supabase.auth.onAuthStateChange(async (event, session) => {
    const prevUid = authUserValue?.uid
    const user = session?.user

    if (user) {
      const isWhitelisted = await checkWhitelist(user.id)
      
      if (!isWhitelisted) {
        console.warn('[AuthStore] User not whitelisted:', user.id)
        authUserValue = null
        authStatusValue = 'blocked'
        await supabase.auth.signOut()
      } else {
        authUserValue = {
          uid: user.id,
          email: user.email ?? null,
          displayName: user.user_metadata?.full_name ?? null,
          isAnonymous: user.is_anonymous ?? false,
          getIdToken: async () => session.access_token,
        }
        authStatusValue = 'signed-in'
        const { persistenceService } = await loadPersistenceService()
        await persistenceService.startUserSync(user.id)
      }
    } else {
      if (prevUid) {
        const { persistenceService } = await loadPersistenceService()
        await persistenceService.stopUserSync(prevUid)
      }
      authUserValue = null
      authStatusValue = 'signed-out'
    }
    notifySubscribers()
  })
}

initAuth()

function e2eGuestUser(): AnyQuestUser {
  return {
    uid: 'e2e_guest',
    email: null,
    isAnonymous: true,
    displayName: 'Guest',
    getIdToken: async () => 'mock-guest-token',
  }
}

function e2eAdminUser(): AnyQuestUser {
  // ... same mock logic ...
  try {
    const raw = localStorage.getItem('am2_auth_user_mock')
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.uid) {
        return {
          uid: String(parsed.uid),
          email: parsed.email ? String(parsed.email) : 'admin@anyquest.com',
          isAnonymous: false,
          displayName: parsed.displayName ? String(parsed.displayName) : 'E2E Admin',
          getIdToken: async () => 'mock-admin-token',
        }
      }
    }
  } catch { /* ignored */ }
  return {
    uid: 'e2e_admin',
    email: 'dharmax@gmail.com',
    isAnonymous: false,
    displayName: 'E2E Admin',
    getIdToken: async () => 'mock-admin-token',
  }
}

export const authStore = {
  get status(): AuthStatus { return authStatusValue; },
  get user(): AnyQuestUser | null { return authUserValue; },

  subscribe(fn: () => void): () => void {
    subscribers.add(fn)
    return () => subscribers.delete(fn)
  },

  async signInWithGoogle(): Promise<void> {
    if (AUTH_FALLBACK_MODE) {
      authUserValue = e2eAdminUser()
      authStatusValue = 'signed-in'
      notifySubscribers()
      return
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        queryParams: { prompt: 'select_account' }
      }
    })
    if (error) throw error
  },

  async signInAsGuest(): Promise<void> {
    if (AUTH_FALLBACK_MODE) {
      try { sessionStorage.setItem(E2E_MOCK_AUTH_KEY, 'guest') } catch { /* ignored */ }
      authUserValue = e2eGuestUser()
      authStatusValue = 'signed-in'
      notifySubscribers()
      return
    }
    const { error } = await supabase.auth.signInAnonymously()
    if (error) throw error
  },

  async signOut(): Promise<void> {
    if (AUTH_FALLBACK_MODE) {
      try { sessionStorage.removeItem(E2E_MOCK_AUTH_KEY) } catch { /* ignored */ }
      authUserValue = null
      authStatusValue = 'signed-out'
      const { clearLogicCache } = await loadLogicCacheRuntime()
      clearLogicCache()
      notifySubscribers()
      return
    }
    await supabase.auth.signOut()
    const { clearLogicCache } = await loadLogicCacheRuntime()
    clearLogicCache()
  },

  async switchAccount(): Promise<void> {
    await this.signOut()
    await this.signInWithGoogle()
  },

  async fetchEntitlement(): Promise<any> {
    const user = this.user
    if (!user) return null
    try {
      const idToken = await user.getIdToken()
      // Note: This endpoint should accept the Supabase access token.
      const response = await fetch('https://anyquest-entitlement-proxy.example.com/entitlement', {
        headers: { 'Authorization': `Bearer ${idToken}` }
      })
      if (!response.ok) return null
      const data = await response.json()
      return data.entitlement
    } catch (e) {
      console.warn('[AuthStore] Failed to fetch entitlement:', e)
      return null
    }
  },
}
